import { type RRule, RRule as RRuleClass } from "rrule";
import { hourMs, minuteMs } from "verdi-common/timeConstants";

import type { DurationBucket, RecurrenceFrequency, RecurrenceProperties, ZoneProperties } from "../events";

/**
 * Shared duration bucket builder for all irrigation events.
 * Keep the raw duration_ms on the event for averages / percentiles.
 */
export const buildDurationBucket = (durationMs: number): DurationBucket => {
    if (durationMs <= 15 * minuteMs) return "lte_15m";
    if (durationMs <= hourMs) return "lte_1h";
    if (durationMs <= 4 * hourMs) return "lte_4h";
    return "gt_4h";
};

/**
 * Shared zone properties builder for all irrigation events.
 * zone_ids is a list (filterable per zone); zone_count mirrors its length.
 */
export const buildZoneProperties = (
    zoneWateringPercentages: Record<string, number> | undefined | null,
): ZoneProperties => {
    const zoneIds = Object.keys(zoneWateringPercentages || {});
    return {
        zone_ids: zoneIds,
        zone_count: zoneIds.length,
    };
};

const FREQ_TO_LABEL: Record<number, RecurrenceFrequency> = {
    [RRuleClass.DAILY]: "daily",
    [RRuleClass.WEEKLY]: "weekly",
    [RRuleClass.MONTHLY]: "monthly",
    [RRuleClass.YEARLY]: "yearly",
};

/**
 * Shared recurrence fields from an RRule.
 * count === 1 is a one-off; frequency / day-count are omitted unless the rule is recurring.
 */
export const buildRecurrenceProperties = (rule: RRule | undefined | null): RecurrenceProperties => {
    const isRecurring = !!rule && rule.options.count !== 1;
    if (!rule || !isRecurring) {
        return { is_recurring: false };
    }
    return {
        is_recurring: true,
        recurrence_frequency: FREQ_TO_LABEL[rule.options.freq],
        days_of_week_count: rule.options.byweekday?.length ?? 0,
    };
};
